import { Page } from "../../components/Page";
import React, { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../helpers/api";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { PopupModal } from "../../components/Modal";
import { ShowToast } from "../../components/toast";
import clsx from "clsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// =============================================================================
// TYPES
// =============================================================================

interface KbEntry {
  id: string;
  title: string;
  content: string;
  category: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const KB_CATEGORIES = [
  "product",
  "pricing",
  "marketing",
  "integration",
  "policy",
  "onboarding",
  "troubleshooting",
  "general",
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/** Standalone content (no Page wrapper) — used inside CustomerSuccess tabs */
export function KnowledgeBaseContent() {
  return <KnowledgeBaseInner />;
}

/** Full page with Page wrapper — standalone route (legacy) */
export function KnowledgeBase() {
  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        <KnowledgeBaseInner />
      </div>
    </Page>
  );
}

function KnowledgeBaseInner() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStaticKb, setShowStaticKb] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KbEntry | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["cs-kb", debouncedSearch, filterCategory, page],
    queryFn: () =>
      api.csKb.list({
        search: debouncedSearch || undefined,
        category: filterCategory || undefined,
        page,
        limit: 50,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.csKb.delete(id),
    onSuccess: () => {
      ShowToast({ type: "success", message: "KB entry deleted" });
      setDeleteConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ["cs-kb"] });
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to delete entry" }),
  });

  const entries: KbEntry[] = data?.data || [];
  const total: number = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const columnHelper = createColumnHelper<KbEntry>();

  const columns = [
    columnHelper.accessor("category", {
      header: "Category",
      cell: (info) => {
        const cat = info.getValue();
        return cat ? (
          <span className="inline-block px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-md capitalize">
            {cat}
          </span>
        ) : (
          <span className="text-xs text-gray-400">Uncategorized</span>
        );
      },
    }),
    columnHelper.accessor("title", {
      header: "Title",
      cell: (info) => (
        <span className="text-sm font-medium text-gray-900">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("content", {
      header: "Content",
      cell: (info) => (
        <p className="text-sm text-gray-600 line-clamp-2 max-w-[400px]">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("updatedAt", {
      header: "Updated",
      cell: (info) => (
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {new Date(info.getValue()).toLocaleString("en-US", {
            timeZone: "America/New_York",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </span>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingEntry(info.row.original);
            }}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
          >
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmId(info.row.original.id);
            }}
            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
          >
            Delete
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable<KbEntry>({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
      <div className="flex-1">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h1 className="text-2xl font-semibold text-content-primary">
            Knowledge Base
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStaticKb(true)}
              className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary border border-[#E9E9EB] rounded hover:bg-surface-hover"
            >
              View Built-in KB
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90"
            >
              + Add Entry
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex px-3 py-2 border border-[#E9E9EB] bg-surface-primary rounded-lg items-center gap-2 min-w-[240px]">
            <SearchIcon className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or content..."
              className="w-full outline-none text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
          >
            <option value="">All Categories</option>
            {KB_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-surface-primary rounded-lg shadow-sm border border-[#E9E9EB]">
          <div className="overflow-auto max-h-[calc(100vh-320px)]">
            <table className="w-full">
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="sticky top-0 z-10">
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={clsx(
                          "text-[0.875rem] font-medium text-[#202020] text-start px-4 py-3 bg-[#F4F4F6]",
                          index === headerGroup.headers.length - 1 &&
                            "rounded-tr-[8px] rounded-br-[8px]",
                          index === 0 && "rounded-tl-[8px] rounded-bl-[8px]"
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {!isLoading &&
                  table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr
                        className="cursor-pointer hover:bg-surface-hover"
                        onClick={() =>
                          setExpandedId(
                            expandedId === row.original.id
                              ? null
                              : row.original.id
                          )
                        }
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 border-b border-b-border-primary font-medium text-[0.8125rem]"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                      {expandedId === row.original.id && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-4 bg-gray-50 border-b border-b-border-primary"
                          >
                            <div className="text-sm text-gray-700 whitespace-pre-wrap max-w-[800px]">
                              {row.original.content}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                {(isLoading || isFetching) && (
                  <TableSkeleton rows={5} columns={5} />
                )}
              </tbody>
            </table>

            {!isLoading && !isFetching && entries.length === 0 && (
              <div className="flex flex-col justify-center items-center py-16">
                <span className="text-content-tertiary text-[1rem] font-medium">
                  No knowledge base entries yet
                </span>
                <span className="text-content-disabled text-[0.875rem] mt-1">
                  Add entries to supplement the AI's built-in product knowledge
                </span>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border-secondary">
              <span className="text-sm text-content-tertiary">
                {total} entr{total !== 1 ? "ies" : "y"} total
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <KbEntryModal
            onClose={() => setShowAddModal(false)}
            onSaved={() => {
              setShowAddModal(false);
              queryClient.invalidateQueries({ queryKey: ["cs-kb"] });
            }}
          />
        )}

        {/* Edit Modal */}
        {editingEntry && (
          <KbEntryModal
            entry={editingEntry}
            onClose={() => setEditingEntry(null)}
            onSaved={() => {
              setEditingEntry(null);
              queryClient.invalidateQueries({ queryKey: ["cs-kb"] });
            }}
          />
        )}

        {/* Delete Confirm */}
        {deleteConfirmId && (
          <PopupModal
            onClose={() => setDeleteConfirmId(null)}
            contentClassName="mx-auto max-w-[400px] w-full"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Delete KB Entry
            </h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this knowledge base entry? The AI
              will no longer have access to this information.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </PopupModal>
        )}

        {/* Built-in Static KB Modal */}
        {showStaticKb && (
          <StaticKbModal onClose={() => setShowStaticKb(false)} />
        )}
      </div>
  );
}

// =============================================================================
// STATIC KB MODAL
// =============================================================================

function StaticKbModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cs-kb-static"],
    queryFn: () => api.csKb.getStatic(),
    staleTime: 1000 * 60 * 30,
    retry: 1,
  });

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto max-w-[800px] w-full"
    >
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-gray-900">
          Built-in Knowledge Base
        </h3>
        <span className="text-xs text-gray-400">Read only</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        This is the static product knowledge baked into the codebase. The AI
        polisher always includes this when crafting responses.
      </p>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-gray-400">
          Loading...
        </div>
      ) : isError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-red-500">
            Failed to load built-in knowledge base
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {(error as any)?.response?.status
              ? `Error ${(error as any).response.status}: ${(error as any).response.statusText}`
              : (error as Error)?.message || "Unknown error"}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-2 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto bg-gray-50 rounded-lg p-4 text-sm text-gray-700 leading-relaxed prose prose-sm prose-gray max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.content || "No content available"}</ReactMarkdown>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </PopupModal>
  );
}

// =============================================================================
// KB ENTRY MODAL (Add / Edit)
// =============================================================================

function KbEntryModal({
  entry,
  onClose,
  onSaved,
}: {
  entry?: KbEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEditing = !!entry;

  const [title, setTitle] = useState(entry?.title || "");
  const [content, setContent] = useState(entry?.content || "");
  const [category, setCategory] = useState(entry?.category || "");

  const createMutation = useMutation({
    mutationFn: () =>
      api.csKb.create({
        title,
        content,
        category: category || undefined,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "KB entry created" });
      onSaved();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to create entry" }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.csKb.update(entry!.id, {
        title,
        content,
        category: category || undefined,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "KB entry updated" });
      onSaved();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to update entry" }),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave = title.trim().length > 0 && content.trim().length > 0;

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto max-w-[640px] w-full"
    >
      <h3 className="text-lg font-semibold text-gray-900">
        {isEditing ? "Edit KB Entry" : "Add KB Entry"}
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        This content will be included in the AI polisher's context when
        generating email responses.
      </p>

      <div className="space-y-4">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          >
            <option value="">Select a category...</option>
            {KB_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder='e.g., "Current Promotion: 20% Off First 3 Months"'
          />
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full h-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y font-mono"
            placeholder={`Write the knowledge that the AI should know. Be specific and factual.\n\ne.g.,\nWe are currently running a promotion: 20% off the first 3 months for new customers signing up before March 31, 2026. This applies to all tiers (Starter, Pro, Max). Existing customers can refer a friend to get 1 month free.`}
          />
          <p className="text-xs text-gray-400 mt-1">
            Be specific, factual, and write as if briefing a colleague. The AI
            will use this as reference when writing email responses.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={() =>
            isEditing ? updateMutation.mutate() : createMutation.mutate()
          }
          disabled={!canSave || isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
        >
          {isPending
            ? "Saving..."
            : isEditing
            ? "Update Entry"
            : "Add Entry"}
        </button>
      </div>
    </PopupModal>
  );
}

// =============================================================================
// TABLE SKELETON
// =============================================================================

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-4 py-4 border-b border-b-border-primary"
            >
              <div className="w-full h-4 bg-surface-disabled rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
